import type { V2Bag, V2Value } from "@/features/applications/api/applications.api";

export type FieldType =
  | "text" | "number" | "yesno" | "checkbox" | "date" | "textarea" | "list" | "select" | "multicheck";

export type RuleName =
  | "ssn" | "taxId" | "routing" | "phone" | "zip" | "email" | "mcc" | "lettersOnly" | "accountMatch" | "accountNumber";

export type Opt = string | { value: string; label: string; hint?: string };

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;

  options?: Opt[];

  section?: string;

  showWhen?: (b: V2Bag) => boolean;

  hint?: string;

  locked?: boolean;

  lockedValue?: boolean;

  required?: boolean;

  placeholder?: string;

  radio?: boolean;

  rule?: RuleName;

  requiredWhen?: (b: V2Bag) => boolean;

  requiredHint?: (b: V2Bag) => boolean;

  presets?: string[];

  disableFuture?: boolean;

  defaultYear?: number;

  max?: number;

  integer?: boolean;

  maxLength?: number;

  tight?: boolean;


  check?: (v: V2Value, b: V2Bag) => string | null;
}

export type RepeatKey = "principals" | "bankAccounts" | "posSetup" | "tradeReferences";

export interface StepDef {
  n: number;
  label: string;

  hidden?: boolean;

  hideEsign?: boolean;

  banner?: string;

  fields?: FieldDef[];

  repeat?: { collection: RepeatKey; title: string; noun?: string; rowFields: FieldDef[]; minRows?: number };

  feeFields?: FieldDef[];

  documents?: boolean;

  review?: boolean;
}

export const f = (key: string, label: string, type: FieldType = "text"): FieldDef => ({ key, label, type });
export const sel = (key: string, label: string, options: Opt[]): FieldDef => ({
  key, label, type: "select", options,
});

export const inSection = (section: string, defs: FieldDef[]): FieldDef[] =>
  defs.map((d) => ({ ...d, section }));

export const tight = (defs: FieldDef[]): FieldDef[] =>
  defs.map((d) => ({ ...d, tight: true }));

export const when = (showWhen: (b: V2Bag) => boolean, d: FieldDef): FieldDef => ({ ...d, showWhen });

export const req = (d: FieldDef): FieldDef => ({ ...d, required: true });

export const reqWhen = (fn: (b: V2Bag) => boolean, d: FieldDef): FieldDef => ({ ...d, requiredWhen: fn });

export const reqHint = (fn: (b: V2Bag) => boolean, d: FieldDef): FieldDef => ({ ...d, requiredHint: fn });

export const withRule = (name: RuleName, d: FieldDef): FieldDef => ({ ...d, rule: name });

export const hint = (text: string, d: FieldDef): FieldDef => ({ ...d, hint: text });

export const radio = (d: FieldDef): FieldDef => ({ ...d, radio: true });

export const dob = (d: FieldDef): FieldDef => ({ ...d, disableFuture: true, defaultYear: 1990 });

export const ph = (text: string, d: FieldDef): FieldDef => ({ ...d, placeholder: text });

export const pct = (d: FieldDef): FieldDef => ({ ...d, max: 100 });

export const nmax = (n: number, d: FieldDef): FieldDef => ({ ...d, max: n });

export const whole = (d: FieldDef): FieldDef => ({ ...d, integer: true });

export const mx = (n: number, d: FieldDef): FieldDef => ({ ...d, maxLength: n });

export interface RateException {
  program: string;
  ratePct: number;
  perTxnFee: number | null;
  reason: string;
  status: "Pending" | "Approved" | "Declined";
  requestedBy: string;
  requestedAt: string;
  decidedBy?: string | null;
  decidedAt?: string | null;
  note?: string | null;
}

export function rateExceptionOf(b: V2Bag): RateException | null {
  const r = b.rateException as unknown;
  return r && typeof r === "object" && typeof (r as RateException).status === "string" ? (r as RateException) : null;
}

export function rateExceptionCovers(b: V2Bag, r: RateException | null = rateExceptionOf(b)): boolean {
  if (!r) return false;
  const fee = typeof b.pricingPerTxnFee === "number" ? b.pricingPerTxnFee : null;
  return r.program === b.pricingProgram && r.ratePct === b.pricingRatePct && (r.perTxnFee ?? null) === fee;
}

export const floorOf = (fn: (b: V2Bag) => number | undefined, d: FieldDef): FieldDef => ({
  ...d,
  check: (v, b) => {
    const min = fn(b);
    const waived = rateExceptionCovers(b) && rateExceptionOf(b)?.status !== "Declined";
    if (min !== undefined && typeof v === "number" && v < min && !waived) return BELOW_FLOOR_MESSAGE;
    return d.check?.(v, b) ?? null;
  },
});

export const BELOW_FLOOR_MESSAGE = "Below the approved rate for this program.";

export const checkOf = (fn: (v: V2Value, b: V2Bag) => string | null, d: FieldDef): FieldDef => ({ ...d, check: fn });

